import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../../api/apiClient';

export interface AppliedFiltersForLoadExecution {
  selectedRegions: Array<{ id: string; displayText: string }>;
  selectedMarkets: Array<{ id: string; displayText: string }>;
  selectedLocations: Array<{ id: string; displayText: string }>;
  selectedLocationTypes: Array<{ id: string; displayText: string }>;
  selectedYears: Array<{ id: string; displayText: string }>;
  selectedPeriods: Array<{ id: string; displayText: string }>;
  selectedWeeks: Array<{ id: string; displayText: string }>;
  deliveryDateRange: { start: string | null; end: string | null };
  selectedDrivers: Array<{ id: string; displayText: string }>;
  selectedStatuses: Array<{ id: string; displayText: string }>;
  selectedOnTime: Array<{ id: string; displayText: string }>;
  selectedArrivedAsExpected: Array<{ id: string; displayText: string }>;
  manifestHourRange: { from: number; to: number };
  search?: string;
}

export interface FiscalSelection {
  year?: number;
  period?: number;
  week?: number;
}

export interface LoadExecutionScorecardsRequest {
  locationId?: string[];
  locationType?: string[];
  fiscalSelections?: FiscalSelection[];
  startDate?: string;
  endDate?: string;
  driver?: string[];
  status?: string[];
  onTime?: string[];
  arrivedAsExpected?: string[];
  manifestHourFrom?: number;
  manifestHourTo?: number;
  search?: string;
}

export interface LoadExecutionScorecardsData {
  completedLoads: number;
  incompleteLoads: number;
  onTimeLoads: number;
  lateLoads: number;
  driverArrivedAsExpectedCount: number;
  driverArrivedAsExpectedDenominator: number;
  plannedLoads: number;
  completedPercentage?: number;
  onTimePercentage?: number;
  driverArrivedAsExpectedPercentage?: number;
}

export interface LoadExecutionScorecardsResponse {
  data: {
    completeLoads: number;
    incompleteLoads: number;
    completedPercentage: number;
    onTimeLoads: number;
    lateLoads: number;
    onTimePercentage: number;
    driverArrivedAsExpectedCount: number;
    driverArrivedOpportunitiesCount: number;
    driverArrivedAsExpectedPercentage: number;
    driverManifestCount: number;
  };
  executionTimeMs: number;
  asOfDate: string;
  isApproximate: boolean;
  message: null | string;
}

const normalizeFilterValue = (value: unknown): string =>
  String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

// "LOCAL ROUTE ID + SEQUENCE" is a display/export-only concatenation (e.g. "301-1 + 1");
// no backend field stores that literal string, so pasting it into search matches nothing
// unless it's stripped back down to the bare localRouteId ("301-1") first.
const ROUTE_SEQUENCE_SUFFIX_PATTERN = /^(.+?)\s*\+\s*\S+$/;
const stripRouteSequenceSuffix = (value: string): string => {
  const match = value.match(ROUTE_SEQUENCE_SUFFIX_PATTERN);
  return match ? match[1].trim() : value;
};

const getLocationLabel = (loc: any): string =>
  String(loc?.locName ?? loc?.locationName ?? loc?.name ?? loc?.locId ?? loc?.locationId ?? '').trim();

function resolveLocationIdsFromFilters(
  filters: AppliedFiltersForLoadExecution,
  adminLocations: any[]
): string[] {
  // ✅ FIX: Use actual location IDs (not normalized) from selectedLocations
  // Normalization was causing IDs to be lost - we need the actual locId values
  const selectedLocationIds = new Set(
    filters.selectedLocations.map((l) => String(l.id).trim()).filter(Boolean)
  );
  if (selectedLocationIds.size > 0) {
    console.log('[resolveLocationIdsFromFilters] Using explicit selected locations:', Array.from(selectedLocationIds));
    return Array.from(selectedLocationIds);
  }

  const selectedRegionIds = new Set(
    filters.selectedRegions.map((r) => normalizeFilterValue(r.id))
  );
  const selectedMarketIds = new Set(
    filters.selectedMarkets.map((m) => normalizeFilterValue(m.id))
  );

  // ✅ If no region/market filters are selected, include ALL locations
  // This ensures initial page load sends all accessible Location IDs
  const resolvedLocations = new Set<string>();
  adminLocations.forEach((loc) => {
    const regionLabel = normalizeFilterValue(String(loc?.region ?? loc?.unit ?? ''));
    const marketLabel = normalizeFilterValue(String(loc?.market ?? loc?.pod ?? loc?.marketPod ?? loc?.marketName ?? ''));
    // `locationId` (EnrichedLocation, from appData.locations) must win over
    // the display-name fallback `getLocationLabel` resolves to — otherwise a
    // location missing a `locId` would incorrectly resolve to its NAME
    // instead of its id. `locId` is kept only for back-compat with any
    // caller still passing AdminLocation-shaped entries.
    const locId = String(loc?.locId ?? loc?.locationId ?? '').trim();

    // ✅ FIX: Match against filters - region and market must match if filters are applied
    // If no filters, include all locations
    const regionMatches = selectedRegionIds.size === 0 || selectedRegionIds.has(regionLabel);
    const marketMatches = selectedMarketIds.size === 0 || selectedMarketIds.has(marketLabel);

    if (regionMatches && marketMatches && locId) {
      resolvedLocations.add(locId);
    }
  });

  const resolvedArray = Array.from(resolvedLocations);
  console.log('[resolveLocationIdsFromFilters] Resolved', resolvedArray.length, 'location IDs from region/market filters:', resolvedArray);
  
  return resolvedArray;
}

const normalizeDateToYYYYMMDD = (dateStr: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [mm, dd, yyyy] = dateStr.split('-');
    return `${yyyy}-${mm}-${dd}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [mm, dd, yyyy] = dateStr.split('/');
    return `${yyyy}-${mm}-${dd}`;
  }
  return dateStr;
};

// Backend's endDate bound is exclusive (confirmed: endDate=startDate returns 0 rows,
// endDate=startDate+1day returns the matching day's rows), so range ends need +1 day.
const addOneDayToYYYYMMDD = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + 1));
  return date.toISOString().slice(0, 10);
};

export function buildLoadExecutionPayload(
  filters: AppliedFiltersForLoadExecution,
  adminLocations?: any[]
): LoadExecutionScorecardsRequest {
  const payload: LoadExecutionScorecardsRequest = {};

  // Resolve location IDs from Region/Market/Location/LocationType filters
  let location: string[] = [];
  if (adminLocations && adminLocations.length > 0) {
    location = resolveLocationIdsFromFilters(filters, adminLocations);
  } else {
    // Fallback: use explicit location selections if no admin locations provided
    location = filters.selectedLocations.map((l) => l.id);
  }

  console.log(
    '[Load Execution API] Resolved',
    location.length,
    'location IDs:',
    location,
    'from filter selections:',
    {
      selectedLocations: filters.selectedLocations.length,
      selectedRegions: filters.selectedRegions.length,
      selectedMarkets: filters.selectedMarkets.length,
    }
  );

  const locationType = filters.selectedLocationTypes.map((lt) => lt.id);
  const year = filters.selectedYears.map((y) => Number(y.id));
  const period = filters.selectedPeriods.map((p) => Number(p.id.replace(/^P/i, '')));
  const week = filters.selectedWeeks.map((w) => Number(w.id.replace(/^W/i, '')));
  const driver = filters.selectedDrivers.map((d) => d.id);
  const status = filters.selectedStatuses.map((s) => s.id);
  // Backend expects the On Time filter as the plain label strings ("On Time" / "Late"),
  // not booleans - sending booleans caused the backend to match zero rows either way.
  const onTime = filters.selectedOnTime.map((ot) => ot.id);
  const arrivedAsExpected = filters.selectedArrivedAsExpected.map((a) => a.id);
  // National Route IDs are displayed as "NR-260190" but the backend expects the bare number.
  const trimmedSearch = filters.search?.trim();
  const search = trimmedSearch
    ? stripRouteSequenceSuffix(trimmedSearch).replace(/^nr-?/i, '').trim()
    : trimmedSearch;

  console.log(
    '[Load Execution API] Building payload - year:',
    year,
    'period:',
    period,
    'week:',
    week,
    'driver:',
    driver,
    'status:',
    status
  );

  // Build payload with only non-empty arrays
  if (location.length > 0) {
    payload.locationId = location;
  }
  if (locationType.length > 0) {
    payload.locationType = locationType;
  }

  // Build fiscalSelections array from years, periods, and weeks.
  // "All" selections must be omitted (not sent as 0) - the backend treats
  // period/week 0 as literal values with no matching fiscal records, which
  // was causing every Load Execution request to return zero results.
  if (year.length > 0 || period.length > 0 || week.length > 0) {
    const fiscalSelections: FiscalSelection[] = [];

    const years = year.length > 0 ? year : [undefined];
    const periods = period.length > 0 ? period : [undefined];
    const weeks = week.length > 0 ? week : [undefined];

    years.forEach((y) => {
      periods.forEach((p) => {
        weeks.forEach((w) => {
          const entry: FiscalSelection = {};
          if (y !== undefined) entry.year = y;
          if (p !== undefined) entry.period = p;
          if (w !== undefined) entry.week = w;
          if (Object.keys(entry).length > 0) {
            fiscalSelections.push(entry);
          }
        });
      });
    });

    if (fiscalSelections.length > 0) {
      payload.fiscalSelections = fiscalSelections;
    }
  }

  if (driver.length > 0) {
    payload.driver = driver;
  }
  if (status.length > 0) {
    payload.status = status;
  }
  if (onTime.length > 0) {
    payload.onTime = onTime;
  }
  if (arrivedAsExpected.length > 0) {
    payload.arrivedAsExpected = arrivedAsExpected;
  }

  // Add manifest hour range if not default
  if (filters.manifestHourRange.from !== 0 || filters.manifestHourRange.to !== 23) {
    payload.manifestHourFrom = filters.manifestHourRange.from;
    payload.manifestHourTo = filters.manifestHourRange.to;
  }

  if (search) {
    payload.search = search;
  }

  // Add delivery date range if present. Backend's endDate is an exclusive upper
  // bound, so a single selected day must be sent as [day, day + 1).
  if (filters.deliveryDateRange.start) {
    const normalizedStart = normalizeDateToYYYYMMDD(filters.deliveryDateRange.start);
    const normalizedEnd = filters.deliveryDateRange.end
      ? normalizeDateToYYYYMMDD(filters.deliveryDateRange.end)
      : normalizedStart;
    payload.startDate = normalizedStart;
    payload.endDate = addOneDayToYYYYMMDD(normalizedEnd);
  }

  return payload;
}

export function normalizeLoadExecutionResponse(raw: LoadExecutionScorecardsResponse): LoadExecutionScorecardsData {
  const d = raw.data;
  const completedLoads = d.completeLoads ?? 0;
  const plannedLoads = (d.completeLoads ?? 0) + (d.incompleteLoads ?? 0);
  const daeCount = d.driverArrivedAsExpectedCount ?? 0;
  const daeDenominator = d.driverManifestCount ?? 0;
  return {
    completedLoads,
    incompleteLoads: d.incompleteLoads ?? 0,
    onTimeLoads: d.onTimeLoads ?? 0,
    lateLoads: d.lateLoads ?? 0,
    driverArrivedAsExpectedCount: daeCount,
    driverArrivedAsExpectedDenominator: daeDenominator,
    plannedLoads,
    completedPercentage: d.completedPercentage ?? 0,
    onTimePercentage: d.onTimePercentage ?? 0,
    driverArrivedAsExpectedPercentage: d.driverArrivedAsExpectedPercentage ?? 0,
  };
}

export interface LoadExecutionDetailRecord {
  dispatchId: string;
  deliveryDate: string;
  location: string;
  locationName: string | null;
  region: string | null;
  market: string | null;
  localRouteId: string;
  sequence: string | null;
  nationalRouteId: string;
  driver: string | null;
  driverManifestArrivalTime: string | null;
  actualArrivalTime: string | null;
  arrivedAsExpected: string;
  actualCompletedTime: string | null;
  highlightCompletionTime?: boolean;
  completionTimeTooltip?: string | null;
  completionTimeHighlightColor?: string | null;
  percentComplete: number;
  plannedPallets?: number | null;
  completedPallets?: number | null;
  onTime: string;
  status: string;
  vehicle?: string | null;
  orderCount?: number | null;
  ssccDetails?: LoadExecutionSsccBreakdownRecord[];
}

export interface LoadExecutionDetailResponseData {
  content: LoadExecutionDetailRecord[];
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  executionTimeMs: number;
  asOfDate: string;
}

export interface LoadExecutionDetailResponse {
  data: LoadExecutionDetailResponseData;
  executionTimeMs: number;
  asOfDate: string;
  isApproximate: boolean;
  message: string | null;
}

export interface LoadExecutionDetailRequest extends LoadExecutionScorecardsRequest {
  page: number;
  size: number;
  sort?: string;
}

export interface LoadExecutionDetailData {
  records: LoadExecutionDetailRecord[];
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

export interface LoadExecutionSsccBreakdownRecord {
  stop: number | null;
  ssccId: string;
  eta: string | null;
  status: string | null;
  vehicle?: string | null;
  orderCount?: number | null;
}

function normalizeNumberValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function getVehicleValue(source: any): string | null {
  const rawVehicle =
    source?.vehicle ??
    source?.vehicleId ??
    source?.loadVehicleId ??
    source?.vehicleNumber ??
    source?.truckId ??
    source?.truckNumber ??
    null;

  if (rawVehicle == null) {
    return null;
  }

  const normalized = String(rawVehicle).trim();
  return normalized ? normalized : null;
}

function getOrderCountValue(source: any): number | null {
  return (
    normalizeNumberValue(source?.orderCount) ??
    normalizeNumberValue(source?.salesOrderCount) ??
    normalizeNumberValue(source?.salesOrdersCount) ??
    normalizeNumberValue(source?.salesOrders)
  );
}

export function normalizeLoadExecutionDetailResponse(
  raw: LoadExecutionDetailResponse
): LoadExecutionDetailData {
  const d = raw.data;
  return {
    records: (d.content ?? []).map((record) => ({
      ...record,
      percentComplete: record.percentComplete ?? 0,
      vehicle: getVehicleValue(record),
      orderCount: getOrderCountValue(record),
      ssccDetails: [],
    })),
    totalElements: d.totalElements ?? 0,
    totalPages: d.totalPages ?? 0,
    pageNumber: d.pageNumber ?? 1,
    pageSize: d.pageSize ?? 25,
  };
}

// Maps frontend SortKey values (LoadExecution.tsx) to backend-supported sort field names.
// NOTE: the "LOCATION" column displays `locationName` (see row.locationName || row.location
// in LoadExecution.tsx), so its sort key must map to the backend's `locationName` field, not
// the raw numeric/ID `location` field. Sorting by `location` (the ID) instead of `locationName`
// was the root cause of ASC/DESC producing results that looked unsorted by name (e.g. a page
// full of a single location whose ID happens to sort first/last, instead of alphabetical order).
const LOAD_EXECUTION_SORT_FIELD_MAP: Record<string, string> = {
  id: 'dispatchId',
  deliveryDate: 'deliveryDate',
  location: 'locationName',
  localRouteId: 'localRouteId',
  nationalRouteId: 'nationalRouteId',
  driver: 'driver',
  manifestArrivalDateTime: 'driverManifestArrivalTime',
  actualArrivalDateTime: 'actualArrivalTime',
  arrivedAsExpected: 'arrivedAsExpected',
  actualCompletedDateTime: 'actualCompletedTime',
  palletCount: 'completedPallets',
  percentComplete: 'percentComplete',
  onTime: 'onTime',
  status: 'status',
};

export function buildLoadExecutionDetailPayload(
  filters: AppliedFiltersForLoadExecution,
  adminLocations: any[],
  pageNumber: number = 1,
  pageSize: number = 25,
  sortBy?: string,
  sortDirection?: 'ASC' | 'DESC'
): LoadExecutionDetailRequest {
  const basePayload = buildLoadExecutionPayload(filters, adminLocations);
  
  const detailPayload: LoadExecutionDetailRequest = {
    ...basePayload,
    page: Math.max(0, pageNumber - 1), // Backend expects 0-based page index
    size: pageSize,
  };

  if (sortBy) {
    const mappedField = LOAD_EXECUTION_SORT_FIELD_MAP[sortBy] ?? sortBy;
    detailPayload.sort = `${mappedField}:${sortDirection ?? 'ASC'}`;
  }

  console.log('[Load Execution Detail API] Built request payload:', detailPayload);
  return detailPayload;
}

export async function fetchLoadExecutionDetail(
  request: LoadExecutionDetailRequest
): Promise<LoadExecutionDetailData> {
  const api = createApiClient();
  try {
    console.log('[Load Execution Detail API] Fetching with request:', request);
    const clientTimestamp = getLocalTimestamp();
    const clientTimezone = getTimezoneOffsetString();
    const response = await api.post(
      '/ontime-dispatch/load-execution',
      request,
      { params: { clientTimestamp, clientTimezone } }
    );
    const normalized = normalizeLoadExecutionDetailResponse(response.data);

    // ⚠️ Intentionally NOT pre-fetching SSCC breakdowns for every incomplete
    // dispatch here. This used to eagerly call
    // `/ontime-dispatch/load-execution/{id}/sscc-breakdown` for every
    // incomplete row on the page as soon as the list loaded — i.e. on every
    // page load / sort / filter change — even though most rows are never
    // expanded. The SSCC breakdown is now fetched lazily, exactly once,
    // only when the user expands an individual Incomplete row (see
    // `fetchLoadExecutionSsccBreakdown` and the expand handler in
    // LoadExecution.tsx). `ssccDetails` stays `[]` here by design.
    console.log('[Load Execution Detail API] Response received:', normalized);
    return normalized;
  } catch (error) {
    console.error('[Load Execution Detail API] Error:', error);
    throw error;
  }
}

export async function fetchLoadExecutionScorecards(request: LoadExecutionScorecardsRequest): Promise<LoadExecutionScorecardsData> {
  const api = createApiClient();
  try {
    const clientTimestamp = getLocalTimestamp();
    const clientTimezone = getTimezoneOffsetString();
    const response = await api.post(
      '/ontime-dispatch/load-execution/scorecards',
      request,
      { params: { clientTimestamp, clientTimezone } }
    );
    const normalized = normalizeLoadExecutionResponse(response.data);
    return normalized;
  } catch (error) {
    console.error('[Load Execution API] Error:', error);
    throw error;
  }
}

function normalizeLoadExecutionSsccBreakdownResponse(
  raw: unknown
): LoadExecutionSsccBreakdownRecord[] {
  const rawObj = raw as any;
  const items = Array.isArray(raw)
    ? raw
    : Array.isArray(rawObj?.data)
      ? rawObj.data
      : Array.isArray(rawObj?.data?.content)
        ? rawObj.data.content
        : Array.isArray(rawObj?.data?.ssccRows)
          ? rawObj.data.ssccRows
        : Array.isArray(rawObj?.data?.ssccBreakdown)
          ? rawObj.data.ssccBreakdown
          : Array.isArray(rawObj?.data?.openSsccs)
            ? rawObj.data.openSsccs
            : Array.isArray(rawObj?.data?.ssccs)
              ? rawObj.data.ssccs
              : Array.isArray(rawObj?.data?.breakdown)
                ? rawObj.data.breakdown
                : Array.isArray(rawObj?.data?.records)
                  ? rawObj.data.records
                  : Array.isArray(rawObj?.data?.items)
                    ? rawObj.data.items
                    : Array.isArray(rawObj?.content)
                      ? rawObj.content
                      : Array.isArray(rawObj?.ssccRows)
                        ? rawObj.ssccRows
                      : Array.isArray(rawObj?.ssccBreakdown)
                        ? rawObj.ssccBreakdown
                        : Array.isArray(rawObj?.openSsccs)
                          ? rawObj.openSsccs
                          : Array.isArray(rawObj?.ssccs)
                            ? rawObj.ssccs
                            : Array.isArray(rawObj?.breakdown)
                              ? rawObj.breakdown
                              : Array.isArray(rawObj?.records)
                                ? rawObj.records
                                : Array.isArray(rawObj?.items)
                                  ? rawObj.items
                                  : [];

  if ((rawObj?.data || rawObj) && items.length === 0) {
    const shape = rawObj?.data ? Object.keys(rawObj.data) : Object.keys(rawObj);
    console.warn(
      '[Load Execution SSCC API] Unrecognized SSCC response shape; returning no rows. Keys:',
      shape
    );
  }

  const metaSource = (raw as any)?.data ?? raw;
  const fallbackVehicle = getVehicleValue(metaSource);
  const fallbackOrderCount = getOrderCountValue(metaSource);

  return items.map((item: any) => ({
    stop:
      normalizeNumberValue(item?.stop) ??
      normalizeNumberValue(item?.stopNumber) ??
      normalizeNumberValue(item?.stopSeq) ??
      normalizeNumberValue(item?.sequence),
    ssccId: String(item?.ssccId ?? item?.sscc ?? item?.ssccCode ?? item?.ssccNumber ?? item?.palletId ?? item?.ssccBarcode ?? item?.barcode ?? ''),
    eta: item?.eta ?? item?.estimatedCompletion ?? item?.estimatedCompletionTime ?? item?.estCompletion ?? item?.estCompletionTime ?? item?.estimatedDeliveryTime ?? null,
    status: item?.status ?? item?.ssccStatus ?? item?.palletStatus ?? item?.loadStatus ?? null,
    vehicle: getVehicleValue(item) ?? fallbackVehicle,
    orderCount: getOrderCountValue(item) ?? fallbackOrderCount,
  }));
}

export function mergeLoadExecutionRecordsWithSsccBreakdowns(
  records: LoadExecutionDetailRecord[],
  ssccBreakdownsByDispatchId: Record<string, LoadExecutionSsccBreakdownRecord[]>
): LoadExecutionDetailRecord[] {
  return records.map((record) => {
    const ssccDetails = ssccBreakdownsByDispatchId[record.dispatchId] ?? [];
    const firstSsccDetail = ssccDetails[0];

    return {
      ...record,
      vehicle: record.vehicle ?? firstSsccDetail?.vehicle ?? null,
      orderCount: record.orderCount ?? firstSsccDetail?.orderCount ?? null,
      ssccDetails,
    };
  });
}

export async function fetchLoadExecutionSsccBreakdown(
  dispatchId: string
): Promise<LoadExecutionSsccBreakdownRecord[]> {
  const api = createApiClient();
  const clientTimestamp = getLocalTimestamp();
  const clientTimezone = getTimezoneOffsetString();

  // Endpoint is POST with a required { dispatchId } body, not a bare GET.
  const response = await api.post(
    `/ontime-dispatch/load-execution/${encodeURIComponent(dispatchId)}/sscc-breakdown`,
    { dispatchId },
    { params: { clientTimestamp, clientTimezone } }
  );

  return normalizeLoadExecutionSsccBreakdownResponse(response.data);
}

// ─── LOAD EXECUTION EXPORT API ──────────────────────────────────────────────────────

/**
 * Helper function to escape CSV values and handle special characters
 * @param value Value to escape
 * @returns Escaped CSV value
 */
function csvEscape(value: unknown): string {
  const str = String(value ?? '');
  if (!/[\",\n]/.test(str)) return str;
  return `"${str.replace(/"/g, '""')}"`;
}

/**
 * Generate CSV Blob from Load Execution detail records
 * This ensures exported data exactly matches the UI table display
 * 
 * @param records Detail records to export
 * @returns CSV file as Blob
 */
export function generateLoadExecutionCSV(records: LoadExecutionDetailRecord[]): Blob {
  return new Blob([buildLoadExecutionCSVContent(records)], {
    type: 'text/csv;charset=utf-8;'
  });
}

export function buildLoadExecutionCSVContent(
  records: LoadExecutionDetailRecord[],
  ssccBreakdownsByDispatchId: Record<string, LoadExecutionSsccBreakdownRecord[]> = {}
): string {
  // Define CSV column headers matching the table, plus SSCC breakdown columns
  const headers = [
    'DISPATCH ID',
    'DELIVERY DATE',
    'LOCATION',
    'LOCAL ROUTE ID + SEQUENCE',
    'NATIONAL ROUTE ID',
    'DRIVER',
    'DRIVER MANIFEST ARRIVAL DATE/TIME',
    'ACTUAL ARRIVAL DATE/TIME',
    'ARRIVED AS EXPECTED',
    'ACTUAL COMPLETED DATE/TIME',
    '% COMPLETE',
    'ON-TIME',
    'STATUS',
    'STOP #',
    'SSCC',
    'EST. COMPLETION',
    'SSCC STATUS',
  ];

  const csvLines = records.flatMap((record) => {
    const sequencePart = record.sequence ? ` + ${record.sequence}` : '';
    const onTimeDisplay = record.onTime === 'On Time' || record.onTime === 'True' ? 'On Time' : 'Late';
    const percentDisplay = `${(record.percentComplete || 0).toFixed(2)}%`;
    const baseRow = [
      record.dispatchId,
      record.deliveryDate,
      record.locationName || record.location || '',
      `${record.localRouteId}${sequencePart}`,
      record.nationalRouteId,
      record.driver || '',
      record.driverManifestArrivalTime || '',
      record.actualArrivalTime || '',
      record.arrivedAsExpected || 'Blank',
      record.actualCompletedTime || '',
      percentDisplay,
      onTimeDisplay,
      record.status || 'Incomplete',
    ];

    const ssccRows = ssccBreakdownsByDispatchId[record.dispatchId] ?? [];
    const isIncomplete = String(record.status ?? '').trim().toLowerCase() !== 'complete';

    if (!isIncomplete || ssccRows.length === 0) {
      return [[...baseRow, '', '', '', ''].map(csvEscape).join(',')];
    }

    return ssccRows.map((ssccRow) =>
      [
        ...baseRow,
        ssccRow.stop ?? '',
        ssccRow.ssccId,
        ssccRow.eta ?? '',
        ssccRow.status ?? '',
      ]
        .map(csvEscape)
        .join(',')
    );
  });

  // Combine headers and rows
  return [headers.join(','), ...csvLines].join('\n');
}

export interface LoadExecutionExportResult {
  csvBlob: Blob;
  ssccFetchAttemptCount: number;
  ssccFetchFailureCount: number;
}


/**
 * Fetch ALL records via the paginated detail endpoint and build a CSV export
 * This ensures exported data matches exactly what's in the UI table (same endpoint, same sort)
 * 
 * @param filters Applied filter snapshot
 * @param adminLocations Admin locations for Region/Market/Location ID resolution
 * @param sortBy Current table sort key (frontend SortKey), mapped to backend field internally
 * @param sortDirection Current table sort direction
 * @returns CSV file plus SSCC enrichment metadata
 */
export async function fetchLoadExecutionExport(
  filters: AppliedFiltersForLoadExecution,
  adminLocations: any[],
  sortBy?: string,
  sortDirection?: 'ASC' | 'DESC'
): Promise<LoadExecutionExportResult> {
  const api = createApiClient();
  try {
    console.log('[Load Execution Export API] Fetching all export records from', '/ontime-dispatch/load-execution');

    // First, get the total count by fetching page 1 with large page size
    const firstPageRequest = buildLoadExecutionDetailPayload(
      filters,
      adminLocations,
      1,
      1000, // Use large page size to minimize API calls
      sortBy,
      sortDirection
    );
    
    const firstPageResponse = await api.post(
      '/ontime-dispatch/load-execution',
      firstPageRequest
    );
    
    const normalized = normalizeLoadExecutionDetailResponse(firstPageResponse.data);
    const allRecords: LoadExecutionDetailRecord[] = [...normalized.records];
    
    console.log(`[Load Execution Export API] Fetched page 1, got ${normalized.records.length} records, total: ${normalized.totalElements}`);
    
    // If there are more records, fetch remaining pages
    if (normalized.totalElements > allRecords.length) {
      const totalPages = Math.ceil(normalized.totalElements / 1000);
      console.log(`[Load Execution Export API] Fetching ${totalPages} pages total`);
      
      for (let pageNum = 2; pageNum <= totalPages; pageNum++) {
        const pageRequest = buildLoadExecutionDetailPayload(
          filters,
          adminLocations,
          pageNum,
          1000,
          sortBy,
          sortDirection
        );
        
        const pageResponse = await api.post(
          '/ontime-dispatch/load-execution',
          pageRequest
        );
        
        const pageNormalized = normalizeLoadExecutionDetailResponse(pageResponse.data);
        allRecords.push(...pageNormalized.records);
        
        console.log(`[Load Execution Export API] Fetched page ${pageNum}, got ${pageNormalized.records.length} records`);
      }
    }
    
    console.log(`[Load Execution Export API] Total records collected: ${allRecords.length}`);

    const incompleteDispatchIds = Array.from(
      new Set(
        allRecords
          .filter((record) => String(record.status ?? '').trim().toLowerCase() !== 'complete')
          .map((record) => record.dispatchId)
          .filter(Boolean)
      )
    );

    const ssccBreakdownResponses = await Promise.all(
      incompleteDispatchIds.map(async (dispatchId) => {
        try {
          const breakdown = await fetchLoadExecutionSsccBreakdown(dispatchId);
          return { dispatchId, breakdown, failed: false };
        } catch (error) {
          console.warn(
            `[Load Execution Export API] Failed to fetch SSCC breakdown for ${dispatchId}`,
            error
          );
          return { dispatchId, breakdown: [] as LoadExecutionSsccBreakdownRecord[], failed: true };
        }
      })
    );

    const ssccFetchFailureCount = ssccBreakdownResponses.filter((row) => row.failed).length;
    const ssccBreakdownEntries = ssccBreakdownResponses.map(
      ({ dispatchId, breakdown }) => [dispatchId, breakdown] as const
    );

    const ssccBreakdownsByDispatchId = Object.fromEntries(ssccBreakdownEntries);
    
    // Generate CSV from all collected records
    const csvBlob = new Blob(
      [buildLoadExecutionCSVContent(allRecords, ssccBreakdownsByDispatchId)],
      { type: 'text/csv;charset=utf-8;' }
    );
    console.log(`[Load Execution Export API] Generated CSV blob, size: ${csvBlob.size} bytes`);
    
    return {
      csvBlob,
      ssccFetchAttemptCount: incompleteDispatchIds.length,
      ssccFetchFailureCount,
    };
  } catch (error) {
    console.error('[Load Execution Export API] Export failed:', error);
    throw error;
  }
}
